import React, { useState } from 'react';
import { DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Loader2, UserPlus, ArrowLeft, CheckCircle } from 'lucide-react';

interface OnboardingAuthProps {
  productData: {
    name: string;
    price: number;
    category: string;
    url?: string;
  };
  onComplete: () => void;
  onBack: () => void;
}

export const OnboardingAuth = ({ productData, onComplete, onBack }: OnboardingAuthProps) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    try {
      // Speichere Produktdaten in localStorage für nach der OAuth-Rückkehr
      localStorage.setItem('onboarding_product', JSON.stringify(productData));
      
      const redirectUrl = window.location.hostname === 'localhost'
        ? `${window.location.origin}/dashboard`
        : 'https://kaufpause.at/dashboard';

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        }
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Google Sign-In Fehler:', error);
      toast.error(error.message || 'Fehler bei der Google-Anmeldung');
      setGoogleLoading(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !fullName.trim()) {
      toast.error('Bitte fülle alle Felder aus');
      return;
    }

    if (password.length < 6) {
      toast.error('Das Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      // 1. Benutzer registrieren
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName.trim(),
          }
        }
      });

      if (signUpError) throw signUpError;

      if (!signUpData.user) {
        throw new Error('Keine Benutzerdaten von der Registrierung erhalten');
      }

      // 2. Warte kurz, damit Trigger das Profil erstellen kann
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 3. Speichere Produkt in localStorage für Dashboard (mit Limit-Check dort)
      localStorage.setItem('onboarding_product', JSON.stringify(productData));

      setRegistrationSuccess(true);
      toast.success('Registrierung erfolgreich! Bitte überprüfe dein E-Mail-Postfach.', {
        duration: 8000
      });

      // Nach 3 Sekunden zum Dashboard navigieren
      setTimeout(() => {
        navigate('/dashboard');
        onComplete();
      }, 3000);

    } catch (error: any) {
      console.error('Registrierungsfehler:', error);
      
      if (error.message.includes('already registered')) {
        toast.error('Diese E-Mail-Adresse ist bereits registriert. Bitte melde dich an.');
      } else if (error.message.includes('Password')) {
        toast.error('Das Passwort muss mindestens 6 Zeichen lang sein.');
      } else if (error.message.includes('email')) {
        toast.error('Bitte gib eine gültige E-Mail-Adresse ein.');
      } else {
        toast.error('Fehler bei der Registrierung. Bitte versuche es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (registrationSuccess) {
    return (
      <>
        <DialogHeader className="text-center pb-6">
          <div className="relative w-20 h-20 mx-auto mb-5">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl rotate-6 opacity-20"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-xl">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
          </div>
          <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 bg-clip-text text-transparent mb-3">
            Willkommen bei KaufPause! 🎉
          </DialogTitle>
          <DialogDescription className="text-gray-600 text-base space-y-4">
            <div className="bg-gradient-to-br from-emerald-50 to-blue-50 p-5 rounded-xl border-2 border-emerald-100">
              <p className="font-semibold text-emerald-900 mb-1">
                ✅ Produkt erfolgreich hinzugefügt!
              </p>
              <p className="text-emerald-800 text-sm">
                "{productData.name}" wartet jetzt 30 Tage auf dich.
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-xl border-2 border-blue-200">
              <p className="text-blue-900 text-sm font-medium flex items-start gap-2">
                <span className="text-lg">📧</span>
                <span><strong>Wichtig:</strong> Bestätige deine E-Mail-Adresse über den Link in deinem Postfach.</span>
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Weiterleitung zum Dashboard...</span>
            </div>
          </DialogDescription>
        </DialogHeader>
      </>
    );
  }

  return (
    <>
      <DialogHeader className="text-center pb-6">
        <div className="relative w-20 h-20 mx-auto mb-5">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl rotate-6 opacity-20"></div>
          <div className="relative w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-xl">
            <UserPlus className="w-9 h-9 text-white" />
          </div>
        </div>
        <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 bg-clip-text text-transparent mb-2">
          Fast geschafft!
        </DialogTitle>
        <DialogDescription className="text-gray-600 text-base leading-relaxed">
          Erstelle deinen Account und <span className="font-semibold text-emerald-700">"{productData.name}"</span> wird automatisch zur Warteliste hinzugefügt
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-5">
        {/* Google Sign Up */}
        <Button
          type="button"
          onClick={handleGoogleSignUp}
          className="w-full bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-300 h-12 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all"
          disabled={loading || googleLoading}
        >
          <div className="flex items-center justify-center">
            <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" className="mr-2">
              <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
              </g>
            </svg>
            {googleLoading ? 'Wird angemeldet...' : 'Mit Google registrieren'}
          </div>
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">oder mit E-Mail</span>
          </div>
        </div>

        {/* E-Mail Registrierung */}
        <form onSubmit={handleEmailSignUp} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-gray-700 font-semibold flex items-center gap-2">
              <span className="text-emerald-600">👤</span>
              Name *
            </Label>
            <Input
              id="fullName"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Max Mustermann"
              className="h-12 border-2 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 rounded-xl transition-all"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700 font-semibold flex items-center gap-2">
              <span className="text-emerald-600">✉️</span>
              E-Mail *
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="max@beispiel.de"
              className="h-12 border-2 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 rounded-xl transition-all"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-700 font-semibold flex items-center gap-2">
              <span className="text-emerald-600">🔒</span>
              Passwort *
            </Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mindestens 6 Zeichen"
              className="h-12 border-2 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 rounded-xl transition-all"
              disabled={loading}
              minLength={6}
            />
          </div>

          <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
            <p className="text-gray-600 text-xs text-center">
              Mit der Registrierung akzeptierst du unsere AGB und Datenschutzerklärung
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onBack}
              className="h-12 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl font-semibold transition-all"
              disabled={loading}
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Zurück
            </Button>
            <Button 
              type="submit"
              disabled={loading}
              className="flex-1 h-12 bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-500 hover:from-emerald-600 hover:via-emerald-700 hover:to-emerald-600 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] rounded-xl font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Wird erstellt...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Account erstellen
                </>
              )}
            </Button>
          </div>
        </form>

        <div className="text-center pt-2">
          <p className="text-sm text-gray-600">
            Du hast bereits einen Account?{' '}
            <button
              onClick={() => {
                // Speichere Produktdaten für Login-Flow
                localStorage.setItem('onboarding_product', JSON.stringify(productData));
                window.location.href = '/';
              }}
              className="text-emerald-600 hover:text-emerald-700 font-medium underline"
            >
              Jetzt anmelden
            </button>
          </p>
        </div>
      </div>
    </>
  );
};
